import {ApolloClient} from '@apollo/client';
import type {BroadcastFn} from '@backstage-components/base';
import {assign, createMachine} from 'xstate';
import {
  AccessCodeInstruction,
  AccessCodeInstructionSchema,
} from './AccessCodeDefinition';
import {
  ResendAccessCode,
  ResendAccessCodeMutation,
  ResendAccessCodeVariables,
} from './gql';

/**
 * @private exported for tests
 */
export interface ViewContext {
  broadcast: BroadcastFn<typeof AccessCodeInstructionSchema>;
  client: ApolloClient<object>;
  /**
   * The error message if there is one. Allows `undefined` or `null` as empty
   * value because XState's `assign` treats `undefined` like "no update".
   */
  error?: string | null;
  code?: string | null;
}

type Typestate =
  | {value: 'login'; context: ViewContext}
  | {value: 'loginPending'; context: ViewContext}
  | {value: 'resend'; context: ViewContext}
  | {value: 'resendPending'; context: ViewContext}
  | {value: 'resendSuccess'; context: ViewContext}
  | {value: 'success'; context: ViewContext};

export const AccessCodeViewMachine = createMachine<
  ViewContext,
  AccessCodeInstruction,
  Typestate
>(
  {
    id: 'AccessCodeResend',
    initial: 'login',
    states: {
      login: {
        on: {
          'AccessCode:resend-view': {target: 'resend', actions: ['clearError']},
          'AccessCode:verify': {
            target: 'loginPending',
            actions: ['clearError'],
          },
        },
      },
      loginPending: {
        on: {
          'AccessCode:on-failure': {
            target: 'login',
            actions: [assign({error: (context, event) => event.meta.reason})],
          },
          'AccessCode:on-success': {target: 'success'},
        },
      },
      resend: {
        on: {
          'AccessCode:login-view': {target: 'login', actions: ['clearError']},
          'AccessCode:resend': {
            target: 'resendPending',
            actions: ['clearError', 'resendAccessCode'],
          },
        },
      },
      resendPending: {
        on: {
          'AccessCode:on-resend-failure': {
            target: 'resend',
            actions: [
              assign({
                error: (context, event) => event.meta.reason,
                code: (context, event) => event.meta.code,
              }),
            ],
          },
          'AccessCode:on-resend-success': {target: 'resendSuccess'},
        },
      },
      resendSuccess: {
        on: {
          'AccessCode:resend-view': {target: 'resend', actions: ['clearError']},
          'AccessCode:verify': {target: 'loginPending'},
        },
      },
      success: {
        type: 'final',
      },
    },
  },
  {
    actions: {
      clearError: assign<ViewContext, AccessCodeInstruction>({
        error: () => null,
      }),
      resendAccessCode: async (context, event) => {
        if (event.type === 'AccessCode:resend') {
          try {
            const result = await context.client.mutate<
              ResendAccessCode,
              ResendAccessCodeVariables
            >({
              mutation: ResendAccessCodeMutation,
              variables: {
                data: {
                  coreId: event.meta.coreId,
                  email: event.meta.email,
                  showId: event.meta.showId,
                },
              },
            });
            const response = result.data?.triggerResendAccessCode;
            if (response && 'id' in response) {
              return context.broadcast({
                type: 'AccessCode:on-resend-success',
                meta: {},
              });
            } else {
              return context.broadcast({
                type: 'AccessCode:on-resend-failure',
                meta: {
                  reason: response ? response.message : 'No response from API',
                  code: response?.code,
                },
              });
            }
          } catch (err) {
            return context.broadcast({
              type: 'AccessCode:on-resend-failure',
              meta: {
                reason:
                  err instanceof Error ? err.message : JSON.stringify(err),
              },
            });
          }
        }
      },
    },
  }
);
